import { i18n } from "../utils/i18n";

const API_BASE_URL = 'https://diamondvpn.online';

class DiamondVPNService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (options.body) {
      config.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Auth methods
  async checkAuth() {
    try {
      const authData = await this.request('/api/check-auth');
      return authData.authenticated === true;
    } catch (error) {
      console.error('Auth check failed:', error);
      return false;
    }
  }


  async login() {
    const referrerId = localStorage.getItem('referrer_id') || sessionStorage.getItem('referrer_id');
    const lang = i18n.getLanguage();

    // Build query params preserving both ref and lang
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang);
    if (referrerId) params.set('ref', referrerId);
    const query = params.toString();

    let loginUrl = `${this.baseURL}/api/login${query ? `?${query}` : ''}`;
    window.location.href = loginUrl;
  }

  async logout() {
    const result = await this.request('/api/logout');
    // Clear client-side session data
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    return result;
  }

  // User methods
  async getUserData() {
    return this.request('/api/user');
  }

  async requestTrial() {
    return this.request('/api/request-trial', {
      method: 'POST',
    });
  }

  // Subscription methods
  async getSubscriptionPlans(language = "ru") {
    return this.request(`/api/subscription-plans?language=${i18n.getLanguage()}`);
  }

  // Payment methods (which are enabled in the admin panel)
  async getPaymentMethods() {
    return this.request('/api/payment-methods');
  }

  // Language methods (authenticated users)
  async getUserLanguage() {
    return this.request('/api/user/language');
  }

  async setUserLanguage(language) {
    return this.request('/api/user/language', {
      method: 'POST',
      body: { language },
    });
  }

  async getRegions() {
    return this.request('/api/regions');
  }

  // Telegram login (web cookie session)
  async telegramLoginRequest(telegramId) {
    return this.request('/api/telegram/login/request', {
      method: 'POST',
      body: { telegram_id: String(telegramId || '').trim() },
    });
  }

  async telegramLoginVerify(telegramId, code) {
    return this.request('/api/telegram/login/verify', {
      method: 'POST',
      body: {
        telegram_id: String(telegramId || '').trim(),
        code: String(code || '').trim(),
      },
    });
  }

  async getNews(offset = 0, limit = 8) {
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    });
    return this.request(`/api/news?${params.toString()}`);
  }

  // Payment methods
  async createPayment(subscriptionId, extraGb = 0) {
    return this.request('/api/create-payment', {
      method: 'POST',
      body: {
        subscription_id: subscriptionId,
        extra_gb: extraGb,
      },
    });
  }

  async createCryptoPayment(subscriptionId, extraGb = 0) {
    return this.request('/api/create-crypto-payment', {
      method: 'POST',
      body: {
        subscription_id: subscriptionId,
        extra_gb: extraGb,
      },
    });
  }

  async getPlategaRecurringSubscription() {
    return this.request('/api/platega/recurring-subscription');
  }

  async createPlategaRecurringSubscription(subscriptionId) {
    return this.request('/api/create-platega-recurring-subscription', {
      method: 'POST',
      body: { subscription_id: subscriptionId },
    });
  }

  async cancelPlategaRecurringSubscription() {
    return this.request('/api/platega/recurring-subscription/cancel', { method: 'POST' });
  }

  async createPlategaPayment(subscriptionId, extraGb = 0) {
    return this.request('/api/create-platega-payment', {
      method: 'POST',
      body: {
        subscription_id: subscriptionId,
        extra_gb: extraGb,
      },
    });
  }

  async createTributePayment(subscriptionId) {
    // Use new endpoint that returns short order_id deep-link
    return this.request('/api/create-tribute-payment', {
      method: 'POST',
      body: {
        subscription_id: subscriptionId,
      },
    });
  }

  async createResetTrafficPayment() {
    return this.request('/api/create-reset-traffic-payment', {
      method: 'POST',
    });
  }

  async createResetTrafficCryptoPayment() {
    return this.request('/api/create-reset-traffic-crypto-payment', {
      method: 'POST',
    });
  }

  async createResetTrafficPlategaPayment() {
    return this.request('/api/create-reset-traffic-platega-payment', {
      method: 'POST',
    });
  }

  async getSubscriptionDevices() {
    return this.request('/api/subscription/devices');
  }

  async recreateSubscriptionUrl() {
    return this.request('/api/subscription-url/recreate', {
      method: 'POST',
    });
  }

  async getTrafficStats(range = 'week') {
    const safeRange = ['day', 'week', 'month'].includes(range) ? range : 'week';
    return this.request(`/api/subscription/traffic-stats?range=${safeRange}`);
  }


  // Helper method to format traffic for display
  formatTraffic(bytes) {
    if (bytes === 0) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  // Support chat
  async getSupportThread() {
    return this.request('/api/support/thread');
  }

  async getSupportMessages(afterId = 0) {
    const query = afterId ? `?after_id=${afterId}` : '';
    return this.request(`/api/support/messages${query}`);
  }

  async sendSupportMessage(text, photo = null) {
    if (photo) {
      const form = new FormData();
      if ((text || '').trim()) form.append('text', text.trim());
      form.append('photo', photo);
      const response = await fetch(`${this.baseURL}/api/support/messages`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      return response.json();
    }
    return this.request('/api/support/messages', {
      method: 'POST',
      body: { text },
    });
  }

}

export default new DiamondVPNService();
